import type { DropinLevel } from './contracts';

export const LEVEL_LADDER = ['B', 'BB', 'BB+', 'A', 'A+'] as const;
export type Level = (typeof LEVEL_LADDER)[number];

/** `BB+ - Oolong/Mango` splits into `BB+` and `['Oolong', 'Mango']`. */
export function splitLevel(highestLevel: string): {
  level: string;
  flavours: string[];
} {
  const [level = '', ...rest] = highestLevel.split(' - ');
  const flavours = rest.join(' - ').trim();
  return { level: level.trim(), flavours: flavours ? flavours.split('/') : [] };
}

export function isLevel(value: string): value is Level {
  return (LEVEL_LADDER as readonly string[]).includes(value);
}

export function levelIndex(level: string): number {
  if (!isLevel(level)) throw new Error(`Unknown level ${level}`);
  return LEVEL_LADDER.indexOf(level);
}

export function levelAbove(level: Level): Level {
  return LEVEL_LADDER[
    Math.min(levelIndex(level) + 1, LEVEL_LADDER.length - 1)
  ]!;
}

export const LEVEL_DROPIN_LEVELS: Record<Level, readonly DropinLevel[]> = {
  B: ['beginner'],
  BB: ['beginner'],
  'BB+': ['intermediate'],
  A: ['intermediate'],
  'A+': ['intermediate'],
};

export const CLINIC_LEVELS = {
  BEG: { level: 'B', name: 'Beginner Clinic' },
  INT: { level: 'BB+', name: 'Intermediate Clinic' },
  'BEG/INT': { level: 'B', name: 'Beginner/Intermediate Clinic' },
  PSC: { level: 'BB+', name: 'Positions Clinic' },
} as const satisfies Record<string, { level: Level; name: string }>;

export const DROPIN_LEVEL_LABEL: Record<DropinLevel, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  all: 'All levels',
};

export const DROPIN_LEVEL_SHORT: Record<DropinLevel, string> = {
  beginner: 'BEG',
  intermediate: 'INT',
  all: 'ALL',
};

export function suggestedDropinLevel(level: Level): DropinLevel {
  return LEVEL_DROPIN_LEVELS[level][0]!;
}

export function levelLabel(highestLevel: string): string {
  return highestLevel.replace(' - ', ' ');
}
